
import React, { useState, useEffect } from 'react';
import api from '../api';
import { useAuth } from '../context/AuthContext';

function Messages() {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messageText, setMessageText] = useState('');
  const [newMessageSubject, setNewMessageSubject] = useState('');

  const { user } = useAuth();

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/messages/conversations');

      if (response.data.success) {
        setConversations(response.data.data);
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching conversations:', error);
      setLoading(false);
    }
  };

  const fetchMessages = async (conversationId, recipientId) => {
    try {
      const response = await api.get(`/api/messages/conversation/${recipientId}`);

      if (response.data.success) {
        setMessages(response.data.conversations || []);
        setSelectedConversation(conversationId);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();

    if (!messageText.trim() && !newMessageSubject.trim()) return;

    try {
      await api.post('/api/messages', {
        recipientId: selectedConversation.recipient._id,
        subject: newMessageSubject || `Re: ${selectedConversation.subject}`,
        body: messageText
      });

      alert('Message sent successfully! Recipient will receive an email notification.');

      // Clear form and refresh messages
      setMessageText('');
      setNewMessageSubject('');
      fetchMessages(selectedConversation._id, selectedConversation.recipient._id);
    } catch (error) {
      console.error('Error sending message:', error);
      alert(error.response?.data?.message || 'Failed to send message. Please try again.');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Messages</h1>

      {loading ? (
        <div className="animate-pulse space-y-4">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="bg-white p-4 rounded-lg shadow h-20"></div>
          ))}
        </div>
      ) : conversations.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">No messages yet</h2>
          <p className="text-gray-600 mb-4">Your conversations will appear here when you start messaging other users.</p>
          <a href="/listings" className="bg-primary text-white px-6 py-2 rounded-md hover:bg-blue-800 transition-colors">
            Browse Listings to Start Messaging
          </a>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversations List */}
          <div className="lg:col-span-1 bg-white rounded-lg shadow-md overflow-hidden">
            <h2 className="text-xl font-semibold text-gray-800 p-4 border-b">Conversations</h2>

            <div className="max-h-[calc(100vh-250px)] overflow-y-auto">
              {conversations.map(conv => (
                <div 
                  key={conv._id}
                  onClick={() => conv.recipient && fetchMessages(conv._id, conv.recipient._id)}
                  className={`p-4 border-b cursor-pointer hover:bg-gray-50 ${selectedConversation?._id === conv._id ? 'bg-blue-50' : ''}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    {conv.recipient && (
                      <>
                        <h3 className="font-medium text-gray-900">{conv.recipient.username}</h3>
                        {conv.unreadCount > 0 && (
                          <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs">
                            {conv.unreadCount} unread
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {conv.subject && (
                    <p className="text-sm text-gray-600 truncate">{conv.subject}</p>
                  )}

                  <p className="text-xs text-gray-400 mt-1">
                    {formatDate(conv.lastMessageAt)}
                  </p>

                  {!conv.recipient && (
                    <p className="text-sm text-gray-500 italic">Loading conversation...</p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Messages Area */}
          <div className="lg:col-span-2 bg-white rounded-lg shadow-md flex flex-col h-[calc(100vh-250px)]">
            {selectedConversation ? (
              <>
                {/* Conversation Header */}
                <div className="p-4 border-b bg-gray-50">
                  {selectedConversation.recipient && (
                    <>
                      <h2 className="text-xl font-semibold text-gray-800 mb-1">
                        {selectedConversation.subject || 'No Subject'}
                      </h2>
                      <div className="flex items-center text-sm text-gray-600">
                        <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        {selectedConversation.recipient.username}
                      </div>
                    </>
                  )}
                </div>

                {/* Messages List */}
                <div className="flex-grow overflow-y-auto p-4">
                  {messages.length === 0 ? (
                    <p className="text-center text-gray-500 italic py-8">No messages in this conversation yet.</p>
                  ) : (
                    <div className="space-y-4">
                      {messages.map(msg => (
                        <div 
                          key={msg._id} 
                          className={`flex ${msg.sender._id === user.userId ? 'justify-end' : 'justify-start'}`}
                        >
                          <div 
                            className={`max-w-[70%] rounded-lg p-3 ${
                              msg.sender._id === user.userId 
                                ? 'bg-primary text-white' 
                                : 'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {msg.subject && msg.subject !== selectedConversation.subject && (
                              <p className="text-sm font-semibold mb-1">{msg.subject}</p>
                            )}

                            <p className="whitespace-pre-wrap">{msg.body}</p>

                            <div className={`text-xs mt-2 ${msg.sender._id === user.userId ? 'text-blue-100' : 'text-gray-500'}`}>
                              {formatDate(msg.createdAt)}
                            </div>

                            {!msg.readStatus && msg.sender._id !== user.userId && (
                              <button
                                onClick={async () => {
                                  try {
                                    await api.put(`/api/messages/mark-read/${selectedConversation._id}`);
                                    fetchMessages(selectedConversation._id, selectedConversation.recipient._id);
                                  } catch (error) {
                                    console.error('Error marking as read:', error);
                                  }
                                }}
                                className="text-xs text-blue-600 hover:underline mt-2"
                              >
                                Mark as read
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Message Input */}
                <form onSubmit={sendMessage} className="p-4 border-t bg-gray-50">
                  {selectedConversation.recipient && (
                    <>
                      {!newMessageSubject && (
                        <button 
                          type="button"
                          onClick={() => setNewMessageSubject('')}
                          className="text-sm text-blue-600 hover:underline mb-2"
                        >
                          Send new message with subject
                        </button>
                      )}

                      {newMessageSubject && (
                        <div className="mb-3">
                          <input
                            type="text"
                            value={newMessageSubject}
                            onChange={(e) => setNewMessageSubject(e.target.value)}
                            placeholder="Enter subject..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary"
                          />
                        </div>
                      )}

                      <textarea
                        value={messageText}
                        onChange={(e) => setMessageText(e.target.value)}
                        placeholder="Type your message here..."
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary focus:border-primary resize-none"
                      ></textarea>

                      <div className="flex justify-between items-center mt-3">
                        {newMessageSubject ? (
                          <button 
                            type="button"
                            onClick={() => setNewMessageSubject('')}
                            className="text-sm text-gray-600 hover:text-gray-800"
                          >
                            Remove subject
                          </button>
                        ) : null}

                        <button
                          type="submit"
                          disabled={!messageText.trim() && !newMessageSubject}
                          className={`px-4 py-2 rounded-md ${
                            !messageText.trim() && !newMessageSubject 
                              ? 'bg-gray-300 cursor-not-allowed' 
                              : 'bg-primary hover:bg-blue-800 text-white'
                          } transition-colors`}
                        >
                          Send Message
                        </button>
                      </div>

                      <p className="text-xs text-gray-500 mt-2">
                        The recipient will receive an email notification. Your email address remains private.
                      </p>
                    </>
                  )}
                </form>
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500 italic">
                Select a conversation to view messages
              </div>
            )}
          </div>
        </div>
      )}

      {/* Email Notification Info */}
      {conversations.length > 0 && (
        <div className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4">
          <p className="text-sm text-blue-700">
            <strong>Tip:</strong> When you send a message, the recipient receives an email notification with a "Reply" button. Clicking this opens a reply form on our website, keeping your email address private!
          </p>
        </div>
      )}
    </div>
  );
}

export default Messages;
